import { RecruitmentProfileRequest } from '../../models/request/recruitment-profile-request';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Recruitment, RecruitmentRecentListing } from '../../models/response/recruitment';

@Component({
  selector: 'ngx-recruitment-dialog',
  templateUrl: './recruitment-dialog.component.html',
  styleUrls: ['./recruitment-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecruitmentDialogComponent implements OnChanges {
  @Input() agent: Recruitment | null = null;
  @Input() loading = false;
  @Input() error = '';
  @Input() saving = false;
  @Input() saveError = '';
  @Input() saveMessage = '';
  @Output() retry = new EventEmitter<void>();
  @Output() closeDetail = new EventEmitter<void>();
  @Output() saveProfile = new EventEmitter<RecruitmentProfileRequest>();
  editing = false;
  validationError = '';
  titlesDraft = '';
  draft: RecruitmentProfileRequest = { name: '', phone: '', positions: [], specialities: '', rate: null };

  constructor(private router: Router, private location: Location) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.agent) { this.cancelEdit(); }
  }

  startEdit(): void {
    if (!this.agent || this.saving) { return; }
    this.draft = {
      name: this.agent.name || '', phone: this.agent.phone || '',
      positions: [...(this.agent.positions || [])], specialities: this.agent.specialities || '',
      rate: this.agent.rate == null ? null : this.agent.rate,
    };
    this.titlesDraft = this.draft.positions.join('\n');
    this.validationError = '';
    this.editing = true;
  }

  cancelEdit(): void {
    this.editing = false;
    this.validationError = '';
  }

  get displayedGci(): number | null {
    if (!this.agent) { return null; }
    if (!this.editing) { return this.agent.gci == null ? null : this.agent.gci; }
    const rate = this.draft.rate;
    const volume = this.agent.totalRecentListingAmount;
    if (rate == null || volume == null || !Number.isFinite(rate) || !Number.isFinite(volume)
      || rate < 0 || rate > 100
      || Math.abs(rate * 10000 - Math.round(rate * 10000)) > 0.000001) {
      return null;
    }
    // Preview only; the saved value is calculated by the API using BigDecimal.
    return Math.round(volume * rate) / 100;
  }

  save(): void {
    if (this.saving || !this.editing) { return; }
    const rate = this.draft.rate;
    if (!this.draft.name.trim()) { this.validationError = 'Agent name is required.'; return; }
    if ((this.draft.phone || '').trim().length > 64) {
      this.validationError = 'Phone must not exceed 64 characters.'; return;
    }
    if (rate != null && (!Number.isFinite(rate) || rate < 0 || rate > 100
      || Math.abs(rate * 10000 - Math.round(rate * 10000)) > 0.000001)) {
      this.validationError = 'Rate must be between 0 and 100, with at most 4 decimal places.'; return;
    }
    this.validationError = '';
    this.saveProfile.emit({ ...this.draft, name: this.draft.name.trim(),
      phone: (this.draft.phone || '').trim() || null,
      specialities: (this.draft.specialities || '').trim() || null,
      positions: Array.from(new Set(this.titlesDraft.split(/\r?\n/).map(value => value.trim()).filter(Boolean))),
    });
  }

  openProperty(listing: RecruitmentRecentListing): void {
    if (this.saving) { return; }
    if (listing.matchStatus === 'MATCHED' && listing.prospectId) {
      const url = this.router.serializeUrl(this.router.createUrlTree(
        ['/pages/prospect/client-list'], { queryParams: { prospectId: listing.prospectId } },
      ));
      window.open(this.location.prepareExternalUrl(url), '_blank', 'noopener');
    }
  }

  matchLabel(listing: RecruitmentRecentListing): string {
    switch (listing.matchStatus) {
      case 'MATCHED': return 'View in Client List';
      case 'AMBIGUOUS': return 'Multiple matching CRM addresses';
      case 'INSUFFICIENT_ADDRESS': return 'Incomplete address for CRM lookup';
      default: return 'No matching property in CRM';
    }
  }

  trackListing(index: number, listing: RecruitmentRecentListing): number {
    return listing.recruitmentRecentListingId != null ? listing.recruitmentRecentListingId : index;
  }
}
